/* eslint-disable no-console */
'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import type {
  AttributesEntity,
  ProductVariantValue,
  VariantsEntity,
} from '@eliadmin/core/src/api/products/types/product-types';
import {
  sortVariantsByAvailability,
  useVariantsStore,
} from '@eliadmin/core/src/stores/variant-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import Image from 'next/image';
import { useEffect, useMemo } from 'react';

const VINTAGE_ATTRIBUTE_ID = 3;

function getVariantAttributePosition(value: ProductVariantValue) {
  const productPosition = (value as ProductVariantValue & { product_position?: number | null })
    .product_position;

  return productPosition ?? value.attribute_position ?? Number.MAX_SAFE_INTEGER;
}

function buildVariantLabel(variant: VariantsEntity) {
  // Prefer server-provided name if present
  const base = (variant.name ?? '').trim();
  if (base) return base;

  // Fallback: join value names
  const parts = (variant.values ?? [])
    .filter(Boolean)
    .sort(
      (a, b) =>
        getVariantAttributePosition(a) - getVariantAttributePosition(b) ||
        a.attribute_id - b.attribute_id,
    )
    .map(v => v.name)
    .filter(Boolean);

  return parts.length ? parts.join(' / ') : `Variant #${variant.id}`;
}

function getVariantVintage(variant: VariantsEntity) {
  return variant.values?.find(value => value.attribute_id === VINTAGE_ATTRIBUTE_ID)?.name?.trim();
}

function getVintageAttributeName(attributes: AttributesEntity[]) {
  return attributes.find(attribute => attribute.id === VINTAGE_ATTRIBUTE_ID)?.name?.trim();
}

function isVariantSelectable(variant: VariantsEntity) {
  // Choose your rule:
  // - allow if quantity > 0 OR availability.sellable
  // - block if neither is true
  return variant.quantity > 0 || !!variant.availability?.sellable;
}

export function ProductVariants() {
  const attributes = useVariantsStore(s => s.product.attributes) || [];
  const variants = useVariantsStore(s => s.product.variants) || [];

  const selectedVariant = useVariantsStore(s => s.selectedVariant);
  const setSelectedVariant = useVariantsStore(s => s.setSelectedVariant);

  // Keep your existing "select first available on mount" behavior,
  // but make it variant-based (no manual selectedValues mutation needed).
  useEffect(() => {
    try {
      if (selectedVariant) return;
      if (!Array.isArray(variants) || variants.length === 0) return;

      const sorted = sortVariantsByAvailability(variants) || [];
      const first = sorted.find(v => v && isVariantSelectable(v)) ?? sorted[0];
      if (!first) return;

      setSelectedVariant(first);
    } catch (e) {
      console.warn('Failed to initialize default variant', e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sortedVariants = useMemo(() => {
    const sorted = sortVariantsByAvailability(variants) || [];
    return sorted;
  }, [variants]);

  if (!attributes.length || !variants.length) return null;

  if (sortedVariants.length > 1)
    return (
      <div className={cn('flex flex-col gap-4', 'ea-product-detail-variants')}>
        <div className={cn('flex flex-col gap-2', 'ea-product-detail-variants__variants')}>
          <p className="text-sm font-semibold text-foreground">
            {getVintageAttributeName(attributes) ?? 'Varianta produktu'}
          </p>
          <div className="flex flex-wrap items-start justify-start gap-2">
            {sortedVariants.map(variant => {
              const active = selectedVariant?.id === variant.id;
              const selectable = isVariantSelectable(variant);
              const vintage = getVariantVintage(variant);
              const shouldShowVintage = !!vintage;

              return (
                <button
                  key={`variant-${variant.id}`}
                  type="button"
                  onClick={() => setSelectedVariant(variant)}
                  disabled={!selectable}
                  title={buildVariantLabel(variant)}
                  className={cn(
                    'overflow-hidden rounded-[6px] border transition-all',
                    shouldShowVintage
                      ? 'h-[36px] min-w-[64px] whitespace-nowrap px-4 text-sm font-semibold sm:h-[40px] sm:min-w-[72px]'
                      : variant.images?.[0]
                        ? 'h-[52px] w-[52px] p-0.5 sm:h-[64px] sm:w-[64px]'
                        : 'h-[28px] w-fit whitespace-nowrap px-3 text-xs font-normal sm:h-[34px] sm:text-sm',
                    active
                      ? shouldShowVintage
                        ? 'cursor-context-menu border-primary bg-primary text-white'
                        : 'cursor-context-menu border-border bg-white text-white'
                      : shouldShowVintage
                        ? 'border-border bg-white text-primary hover:border-primary hover:bg-secondary'
                        : 'border-white bg-transparent hover:bg-border',
                    selectable ? 'cursor-pointer' : 'cursor-not-allowed opacity-50',
                  )}
                >
                  {shouldShowVintage ? (
                    vintage
                  ) : variant.images?.[0] ? (
                    <Image
                      src={variant.images[0].url}
                      alt={resolveImageAlt(variant?.images?.[0], buildVariantLabel(variant))}
                      width={64}
                      height={64}
                      className="h-full w-full rounded-[4px] object-contain"
                    />
                  ) : (
                    buildVariantLabel(variant)
                  )}
                </button>
              );
            })}
          </div>
          <p className="text-sm font-light text-[#666464]">Varianta: {selectedVariant?.code}</p>
        </div>

        {/* Optional: keep the attribute UI below as read-only/visual confirmation,
          or keep it interactive if you still want users to adjust after selecting a variant. */}
        {/* If you want to remove it entirely, delete everything below. */}
      </div>
    );
}
